/**
 * useJobTitles Hook (V86)
 *
 * httpsCallable을 통해 직책(Job Title) 마스터 데이터를 조회하는 Hook입니다.
 *
 * **V86 변경:**
 * - Firestore 직접 접근 → httpsCallable 방식으로 전환
 * - 실시간 구독 → 일회성 조회로 변경
 *
 * **기능:**
 * - 백엔드 getJobTitles callable 함수 호출
 * - 검색어 필터링
 * - 사용 여부(status) 필터링
 * - order 기준 정렬
 *
 * **사용 예시:**
 * ```tsx
 * const { jobTitles, isLoading, error } = useJobTitles({
 *   searchText: '팀장',
 *   status: 'active'
 * });
 * ```
 */

import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import { JobTitle } from '../types/jobTitle';
import { getCached, setCached, CACHE_KEY } from '../utils/masterDataCache';

/**
 * useJobTitles Hook 옵션 타입
 */
export interface UseJobTitlesOptions {
  /** 검색어 (직책명, 코드 검색) */
  searchText?: string;

  /** 사용 여부 필터 (all | active | inactive) */
  status?: string;
}

/**
 * useJobTitles Hook 반환 타입
 */
export interface UseJobTitlesResult {
  /** 직책 목록 */
  jobTitles: JobTitle[];

  /** 로딩 상태 */
  isLoading: boolean;

  /** 에러 */
  error: Error | null;

  /** 새로고침 */
  refetch: () => void;
}

/**
 * 직책 마스터 조회 Hook
 *
 * @param options 조회 옵션
 * @returns UseJobTitlesResult
 */
export function useJobTitles(
  options: UseJobTitlesOptions = {}
): UseJobTitlesResult {
  const { searchText = '', status = 'all' } = options;

  const [jobTitles, setJobTitles] = useState<JobTitle[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  useEffect(() => {
    // Firebase 미초기화 시 빈 배열 반환
    if (!functions) {
      setJobTitles([]);
      setIsLoading(false);
      return;
    }

    // V86: httpsCallable을 통한 조회
    const fetchJobTitles = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // V259: 캐시 키 — status + searchText 조합
        const cacheKey = `${CACHE_KEY.JOB_TITLES}${status || 'all'}:${searchText || ''}`;
        const cached = getCached<JobTitle[]>(cacheKey);

        if (cached) {
          setJobTitles(cached);
          setIsLoading(false);
          return;
        }

        const getJobTitlesFn = httpsCallable<
          { status?: string; searchText?: string },
          { success: boolean; jobTitles: JobTitle[] }
        >(functions!, 'getJobTitles');

        const result = await getJobTitlesFn({ status, searchText });

        if (result.data.success) {
          setJobTitles(result.data.jobTitles);
          // V259: 캐시 저장
          setCached(cacheKey, result.data.jobTitles);
        } else {
          throw new Error('직책 목록 조회 실패');
        }

        setIsLoading(false);
      } catch (err: any) {
        console.error('[useJobTitles] 직책 조회 실패:', err);
        setError(err as Error);
        setIsLoading(false);
      }
    };

    fetchJobTitles();
  }, [searchText, status, refetchTrigger]);

  /**
   * 새로고침
   */
  const refetch = () => {
    setRefetchTrigger((prev) => prev + 1);
  };

  return {
    jobTitles,
    isLoading,
    error,
    refetch,
  };
}

/**
 * ============================================
 * 직책 Map Hook (ID → Name 변환용)
 * ============================================
 *
 * 직책 ID를 직책명으로 빠르게 변환하기 위한 Map을 제공합니다.
 *
 * **사용 예시:**
 * ```tsx
 * const jobTitleMap = useJobTitleMap();
 *
 * const jobTitleName = jobTitleMap.get('jt_team_leader'); // "팀장"
 * ```
 */
export function useJobTitleMap(): Map<string, string> {
  const { jobTitles } = useJobTitles({ status: 'active' });

  const [jobTitleMap, setJobTitleMap] = useState<Map<string, string>>(
    new Map()
  );

  useEffect(() => {
    const map = new Map<string, string>();
    jobTitles.forEach((jt) => {
      map.set(jt.id, jt.name);
    });
    setJobTitleMap(map);
  }, [jobTitles]);

  return jobTitleMap;
}

/**
 * ============================================
 * 단일 직책 조회 Hook
 * ============================================
 *
 * 특정 ID의 직책 정보를 조회합니다.
 *
 * **사용 예시:**
 * ```tsx
 * const { jobTitle, isLoading } = useJobTitle('jt_team_leader');
 * ```
 */
export function useJobTitle(
  jobTitleId: string | undefined
): {
  jobTitle: JobTitle | null;
  isLoading: boolean;
  error: Error | null;
} {
  const { jobTitles, isLoading, error } = useJobTitles();

  const [jobTitle, setJobTitle] = useState<JobTitle | null>(null);

  useEffect(() => {
    if (!jobTitleId) {
      setJobTitle(null);
      return;
    }

    const found = jobTitles.find((jt) => jt.id === jobTitleId);
    setJobTitle(found || null);
  }, [jobTitleId, jobTitles]);

  return {
    jobTitle,
    isLoading,
    error,
  };
}
